import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { AnnexureHEditor } from "@/components/franchise-exit/AnnexureHEditor";
import { SettlementV2Modal, ExitChecklistsPanel, CertificateReadinessBanner, AnnexureHPreviewModal, DraftPreviewModal, CompleteExitPackPreviewModal } from './FranchiseExitV2';
import AssetHandoverCompact from './AssetHandoverCompact';
import { toast } from 'sonner';
import { 
  FileText, AlertTriangle, CheckCircle, Clock, Users, Building, 
  DollarSign, ClipboardCheck, Download, Plus, Trash2, X, Loader2,
  FileSignature, Package, Calculator, Award, ArrowRight, RefreshCw, Eye, Unlock, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EXIT_REASONS = [
  { value: 'losses', label: 'Continuous Operational Losses' },
  { value: 'voluntary', label: 'Voluntary Closure by Franchisee' },
  { value: 'mutual_decision', label: 'Mutual Decision' },
  { value: 'breach', label: 'Breach of Agreement' },
  { value: 'other', label: 'Other Reasons' }
];

const ASSET_CATEGORIES = [
  { key: 'kitchen_equipment', label: 'Kitchen Equipment', icon: '🍳' },
  { key: 'furniture_fixtures', label: 'Furniture & Fixtures', icon: '🪑' },
  { key: 'utensils_machinery', label: 'Utensils & Machinery', icon: '🔧' },
  { key: 'food_inventory', label: 'Food Inventory', icon: '🍲' },
  { key: 'packaging_materials', label: 'Packaging Materials', icon: '📦' },
  { key: 'other_assets', label: 'Other Assets', icon: '📋' }
];

const STATUS_COLORS = {
  initiated: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  pending_signatures: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

// iter198 — inline preview block used by the Signature Preview modal.
function SigPreviewBlock({ label, entries, empty, isConsultant }) {
  const list = (entries || []).filter(e => e && (e.signer_name || e.status));
  if (list.length === 0) {
    return (
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</p>
        <p className="text-xs text-red-500 mt-1">PENDING — {empty}</p>
      </div>
    );
  }
  return (
    <div className="p-3 bg-white rounded-lg border border-slate-200">
      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{label}</p>
      <div className="space-y-2">
        {list.map((e, i) => {
          const st = e.status || (e.signed ? 'SIGNED' : 'PENDING');
          const badgeCls = st === 'SIGNED'
            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
            : st === 'PENDING'
            ? 'bg-red-100 text-red-700 border-red-200'
            : 'bg-slate-100 text-slate-600 border-slate-200';
          return (
            <div key={i} className="flex items-start justify-between gap-3 border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
              <div className="flex-1">
                <p className="font-medium text-sm">{e.signer_name || '[Not entered]'}</p>
                <p className="text-xs text-slate-500">
                  {e.signer_designation || e.capacity || 'Signatory'}
                  {e.signature_date ? ` · ${String(e.signature_date).slice(0, 10)}` : ''}
                </p>
                {isConsultant && (
                  <p className="text-[10px] text-amber-600 mt-0.5">Signing as representative — NOT a contractual party</p>
                )}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badgeCls}`}>{st}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}



export default function FranchiseExit() {
  const { session } = useAuth();
  const token = session?.token;
  const isFranchiseOwner = session?.role_key === "franchise_owner";
  const [exits, setExits] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExit, setSelectedExit] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  
  // Modal states
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showSettlementV2, setShowSettlementV2] = useState(false);
  const [showChecklists, setShowChecklists] = useState(false);
  const [showAnnexureH, setShowAnnexureH] = useState(false);
  const [previewSection, setPreviewSection] = useState(null);
  const [showCompletePack, setShowCompletePack] = useState(false);

  // FR-007 P0 — Critical Dates (SA-only) + read-only display
  const [criticalDates, setCriticalDates] = useState(null);
  const [showCriticalDatesModal, setShowCriticalDatesModal] = useState(false);
  const [cdForm, setCdForm] = useState({ franchise_agreement_date: '', effective_exit_date: '', reason: '', remarks: '' });
  const [savingCritical, setSavingCritical] = useState(false);
  const [showExitDiagnostic, setShowExitDiagnostic] = useState(false);
  const [exitStatusData, setExitStatusData] = useState(null);
  const loadCriticalDates = useCallback(async (exitId) => {
    if (!exitId) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/critical-dates-view/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (d.success) setCriticalDates(d);
    } catch (e) { /* silent */ }
  }, [token]);
  const loadExitStatus = useCallback(async (exitId) => {
    if (!exitId) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/exit-status/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      setExitStatusData(d);
    } catch (e) { /* silent */ }
  }, [token]);
  const openCriticalDatesModal = () => {
    setCdForm({
      franchise_agreement_date: (criticalDates?.franchise_agreement_date || '').slice(0, 10),
      effective_exit_date: (criticalDates?.effective_exit_date || '').slice(0, 10),
      reason: '', remarks: '',
    });
    setShowCriticalDatesModal(true);
  };
  const saveCriticalDates = async () => {
    if (!selectedExit) return;
    if (!cdForm.reason.trim()) { toast.error('Reason is mandatory'); return; }
    if (!cdForm.franchise_agreement_date && !cdForm.effective_exit_date) { toast.error('Enter at least one date'); return; }
    setSavingCritical(true);
    try {
      const body = { token, reason: cdForm.reason.trim(), remarks: cdForm.remarks };
      if (cdForm.franchise_agreement_date) body.franchise_agreement_date = cdForm.franchise_agreement_date;
      if (cdForm.effective_exit_date) body.effective_exit_date = cdForm.effective_exit_date;
      const r = await fetch(`${API}/api/franchise-exit/critical-dates/${selectedExit.exit_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(d.changed ? `Saved — ${(d.changes || []).length} change(s)` : 'Dates already up to date');
      setShowCriticalDatesModal(false);
      loadCriticalDates(selectedExit.exit_id);
      fetchExitDetails(selectedExit.exit_id);
    } catch (e) { toast.error(e.message); }
    finally { setSavingCritical(false); }
  };

  // iter242 — SA-only Legal Fields Modal (6 legal override fields)
  const [showLegalFieldsModal, setShowLegalFieldsModal] = useState(false);
  const [lfForm, setLfForm] = useState({
    landlord_release_status: '', landlord_release_notes: '',
    security_deposit_status: '', security_deposit_notes: '',
    landlord_inspection_status: '', handover_exceptions_note: '',
    salary_paid_at: '', salary_amount: '',
    reason: '',
  });
  const [savingLegal, setSavingLegal] = useState(false);
  const openLegalFieldsModal = () => {
    const fin = selectedExit?.financial_settlement || {};
    setLfForm({
      landlord_release_status: selectedExit?.landlord_release_status || '',
      landlord_release_notes: selectedExit?.landlord_release_notes || '',
      security_deposit_status: selectedExit?.security_deposit_status || '',
      security_deposit_notes: selectedExit?.security_deposit_notes || '',
      landlord_inspection_status: selectedExit?.landlord_inspection_status || '',
      handover_exceptions_note: selectedExit?.handover_exceptions_note || '',
      salary_paid_at: (fin.salary_paid_at || '').slice(0, 10),
      salary_amount: fin.salary_amount != null ? String(fin.salary_amount) : '',
      reason: '',
    });
    setShowLegalFieldsModal(true);
  };
  const saveLegalFields = async () => {
    if (!selectedExit) return;
    if (!lfForm.reason.trim()) { toast.error('Reason is mandatory for audit trail'); return; }
    setSavingLegal(true);
    try {
      const body = { token, reason: lfForm.reason.trim() };
      if (lfForm.landlord_release_status) body.landlord_release_status = lfForm.landlord_release_status;
      if (lfForm.landlord_release_notes !== '') body.landlord_release_notes = lfForm.landlord_release_notes;
      if (lfForm.security_deposit_status) body.security_deposit_status = lfForm.security_deposit_status;
      if (lfForm.security_deposit_notes !== '') body.security_deposit_notes = lfForm.security_deposit_notes;
      if (lfForm.landlord_inspection_status) body.landlord_inspection_status = lfForm.landlord_inspection_status;
      if (lfForm.handover_exceptions_note !== '') body.handover_exceptions_note = lfForm.handover_exceptions_note;
      if (lfForm.salary_paid_at) body.salary_paid_at = lfForm.salary_paid_at;
      if (lfForm.salary_amount !== '') body.salary_amount = parseFloat(lfForm.salary_amount);
      const r = await fetch(`${API}/api/franchise-exit/legal-fields/${selectedExit.exit_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(d.changed ? `Saved — ${(d.fields_updated || []).length} field(s) updated` : 'No changes');
      setShowLegalFieldsModal(false);
      fetchExitDetails(selectedExit.exit_id);
    } catch (e) { toast.error(e.message); }
    finally { setSavingLegal(false); }
  };
  
  // Form states
  const [initiateForm, setInitiateForm] = useState({
    franchise_code: '',
    exit_reason: '',
    exit_reason_details: '',
    effective_date: '',
    initiated_by: 'franchisor'
  });
  
  const [assetForm, setAssetForm] = useState({
    kitchen_equipment: [],
    furniture_fixtures: [],
    utensils_machinery: [],
    food_inventory: [],
    packaging_materials: [],
    other_assets: [],
    condition_notes: ''
  });
  
  const [financeForm, setFinanceForm] = useState({
    working_capital_balance: 0,
    staff_salary_current: 0,
    shop_rental_current: 0,
    vendor_payments: 0,
    utility_bills: 0,
    other_dues: 0,
    settlement_notes: ''
  });
  
  const [signatureForm, setSignatureForm] = useState({
    // Franchisor signatories
    sandeep_selected: false,
    jayanti_selected: false,
    // Accounts signatory (iter198 — Shashikant Pande, pulled from Accounts config)
    accounts_selected: false,
    accounts_signer_name: '',
    accounts_signer_designation: '',
    // Exit manager
    exit_manager_name: '',
    exit_manager_designation: 'Exit Manager',
    // Franchisee directors (auto-pulled)
    franchisee_directors: [],
    // Optional: Lease / Property Owner
    lease_owner_signer_name: '',
    lease_owner_capacity: '',
    lease_owner_mobile: '',
    lease_owner_email: '',
    lease_owner_signed: false,
    // Optional: Franchisee consultants / representatives (multi)
    consultants: [] // [{signer_name, organisation, capacity, representing, mobile, email, signed}]
  });
  const [accountsSignatoryConfig, setAccountsSignatoryConfig] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [franchiseDirectors, setFranchiseDirectors] = useState([]);
  
  const [complianceForm, setComplianceForm] = useState({
    no_pending_payments: false,
    brand_assets_transferred: false,
    financial_report_signed: false,
    handover_report_signed: false
  });

  // Fetch exits
  const fetchExits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        setExits(data.exits || []);
      }
    } catch (error) {
      toast.error('Failed to fetch exit records');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch franchises
  const fetchFranchises = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/franchises/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.franchises) {
        setFranchises(data.franchises.filter(f => f.status === 'Active'));
      }
    } catch (error) {
      console.error('Failed to fetch franchises');
    }
  }, [token]);

  useEffect(() => {
    fetchExits();
    if (!isFranchiseOwner) fetchFranchises();
  }, [fetchExits, fetchFranchises, isFranchiseOwner]);

  useEffect(() => {
    if (selectedExit?.exit_id) {
      loadCriticalDates(selectedExit.exit_id);
      loadExitStatus(selectedExit.exit_id);
    } else {
      setCriticalDates(null); setExitStatusData(null);
    }
  }, [selectedExit?.exit_id, loadCriticalDates, loadExitStatus]);

  // Initiate Exit
  const handleInitiateExit = async () => {
    if (!initiateForm.franchise_code || !initiateForm.exit_reason || !initiateForm.effective_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...initiateForm })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Exit process initiated successfully');
        setShowInitiateModal(false);
        setInitiateForm({
          franchise_code: '',
          exit_reason: '',
          exit_reason_details: '',
          effective_date: '',
          initiated_by: 'franchisor'
        });
        fetchExits();
      } else {
        toast.error(data.detail || 'Failed to initiate exit');
      }
    } catch (error) {
      toast.error('Failed to initiate exit');
    } finally {
      setLoading(false);
    }
  };

  // Update Asset Handover
  const handleUpdateAssets = async () => {
    if (!selectedExit) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/update-asset-handover/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...assetForm })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Asset handover updated');
        setShowAssetModal(false);
        fetchExitDetails(selectedExit.exit_id);
      } else {
        toast.error(data.detail || 'Failed to update assets');
      }
    } catch (error) {
      toast.error('Failed to update assets');
    } finally {
      setLoading(false);
    }
  };

  // Update Financial Settlement
  const handleUpdateFinance = async () => {
    if (!selectedExit) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/update-financial-settlement/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...financeForm })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Financial settlement updated');
        setShowFinanceModal(false);
        fetchExitDetails(selectedExit.exit_id);
      } else {
        toast.error(data.detail || 'Failed to update settlement');
      }
    } catch (error) {
      toast.error('Failed to update settlement');
    } finally {
      setLoading(false);
    }
  };

  // iter228 — Certificate eligibility (from /exit-status) surfaced to
  // the Certificate tile so it auto-activates the instant every gate
  // passes, without waiting for a browser reload.
  const [certEligible, setCertEligible] = useState(false);

  // iter229 — SA Unlock / Refresh / Re-freeze Final Settlement
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockConfirmed, setUnlockConfirmed] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const handleUnlockSettlement = async () => {
    if (!selectedExit) return;
    if (!unlockReason.trim()) { toast.error('Reason is mandatory'); return; }
    if (!unlockConfirmed) { toast.error('Please confirm the acknowledgement'); return; }
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/settlement/unlock/${selectedExit.exit_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: unlockReason.trim(), confirmed: true }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.detail || 'Unlock failed');
      toast.success('Settlement unlocked — refresh from ERP and freeze again to release the gate.');
      setShowUnlockModal(false);
      setUnlockReason(''); setUnlockConfirmed(false);
      fetchExitDetails(selectedExit.exit_id);
    } catch (e) {
      toast.error(e.message || 'Unlock failed');
    } finally { setUnlocking(false); }
  };
  const handleFreezeSettlement = async () => {
    if (!selectedExit) return;
    setFreezing(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/settlement/freeze/${selectedExit.exit_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: 'SA unlock → refresh → freeze' }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.detail || 'Freeze failed');
      toast.success(`Settlement re-frozen (v${d.previous_version} → v${d.version}). Final amount: ₹${(d.final_amount || 0).toLocaleString()}`);
      fetchExitDetails(selectedExit.exit_id);
    } catch (e) {
      toast.error(e.message || 'Freeze failed');
    } finally { setFreezing(false); }
  };

  // iter228 — Asset Handover repair history drawer (SA only)
  const [showAHHistory, setShowAHHistory] = useState(false);
  const [ahHistory, setAHHistory] = useState([]);
  const loadAHHistory = async () => {
    if (!selectedExit) return;
    try {
      const res = await fetch(`${API}/api/franchise-exit/asset-snapshot/history/${selectedExit.exit_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      setAHHistory(d?.history || []);
    } catch (e) { setAHHistory([]); }
  };
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairReason, setRepairReason] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  // iter225 — Reopen / Rebuild Asset Handover (finalised)
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  const handleReopenHandover = async () => {
    if (!selectedExit) return;
    if (!reopenReason.trim()) {
      toast.error('Please record a reason to reopen (audit trail).');
      return;
    }
    setReopening(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/asset-snapshot/reopen/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: reopenReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Reopen failed');
      toast.success(`Asset Handover reopened. ${data.row_count_preserved} rows preserved (version ${data.version_superseded} superseded).`);
      setShowReopenModal(false);
      setReopenReason('');
      fetchExitDetails(selectedExit.exit_id);
    } catch (error) {
      toast.error(error.message || 'Reopen failed');
    } finally {
      setReopening(false);
    }
  };
  const handleRepairSettlement = async () => {
    if (!selectedExit) return;
    if (!repairReason.trim()) {
      toast.error('Please record a reason for the data repair (audit trail).');
      return;
    }
    setRepairing(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/settlement/refresh-from-erp/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, repair_reason: repairReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Refresh failed');
      setRepairResult(data);
      toast.success(
        data.was_finalized
          ? 'Finalised settlement data repaired from ERP sources.'
          : 'Settlement worksheet refreshed from ERP sources.'
      );
      fetchExitDetails(selectedExit.exit_id);
    } catch (error) {
      toast.error(error.message || 'Refresh failed');
    } finally {
      setRepairing(false);
    }
  };

  // Migrate signatures for current exit (pull from master data)
  const handleMigrateSignatures = async () => {
    if (!selectedExit) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/migrate-signatures/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Signatures updated from master data');
        fetchExitDetails(selectedExit.exit_id);
      } else {
        toast.error(data.detail || 'Migration failed');
      }
    } catch (error) {
      toast.error('Failed to migrate signatures');
    } finally {
      setLoading(false);
    }
  };

  // Migrate ALL exit signatures at once
  const handleMigrateAllSignatures = async () => {
    if (!window.confirm('This will update signature structure for ALL existing exit records. Continue?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/migrate-all-signatures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.results?.length || 0} exit records updated`);
        if (selectedExit) fetchExitDetails(selectedExit.exit_id);
      } else {
        toast.error(data.detail || 'Migration failed');
      }
    } catch (error) {
      toast.error('Failed to migrate');
    } finally {
      setLoading(false);
    }
  };

  // Fetch franchise directors when opening signature modal
  const fetchFranchiseDirectors = useCallback(async (franchiseCode) => {
    try {
      const res = await fetch(`${API}/api/franchise-exit/franchise-directors/${franchiseCode}`);
      const data = await res.json();
      setFranchiseDirectors(data.directors || []);
    } catch {
      setFranchiseDirectors([]);
    }
  }, []);

  // Open signature modal with pre-populated data
  const openSignatureModal = () => {
    if (!selectedExit) return;
    const sigs = selectedExit.signatures || {};
    
    // Pre-fill from existing data
    const existingFranchisor = sigs.franchisor_signatories || [];
    const existingManager = sigs.exit_manager || null;
    const existingAccounts = sigs.accounts_signatory || null;
    const existingLease = sigs.lease_owner || null;
    const existingConsultants = sigs.franchisee_consultants || [];

    setSignatureForm({
      sandeep_selected: existingFranchisor.some(s => s.signer_name === 'Sandeep Gadhwal'),
      jayanti_selected: existingFranchisor.some(s => s.signer_name === 'Jayanti Kathale'),
      accounts_selected: !!(existingAccounts && (existingAccounts.signed || existingAccounts.signer_name)),
      accounts_signer_name: existingAccounts?.signer_name || '',
      accounts_signer_designation: existingAccounts?.signer_designation || '',
      exit_manager_name: existingManager?.signer_name || selectedExit.initiated_by_user || '',
      exit_manager_designation: existingManager?.signer_designation || 'Exit Manager',
      franchisee_directors: [],
      lease_owner_signer_name: existingLease?.signer_name || '',
      lease_owner_capacity: existingLease?.capacity || '',
      lease_owner_mobile: existingLease?.mobile || '',
      lease_owner_email: existingLease?.email || '',
      lease_owner_signed: !!existingLease?.signed,
      consultants: existingConsultants.map(c => ({
        signer_name: c.signer_name || '',
        organisation: c.organisation || '',
        capacity: c.capacity || c.signer_designation || 'Consultant / Authorised Representative',
        representing: c.representing || '',
        mobile: c.mobile || '',
        email: c.email || '',
        signed: !!c.signed,
      })),
    });

    // Fetch directors + accounts signatory config
    fetchFranchiseDirectors(selectedExit.franchise_code);
    // Pull Accounts signatory once from server config (never hardcoded).
    fetch(`${API}/api/franchise-exit/accounts-signatory`)
      .then(r => r.json())
      .then(data => {
        setAccountsSignatoryConfig(data);
        setSignatureForm(p => ({
          ...p,
          accounts_signer_name: p.accounts_signer_name || data.signer_name,
          accounts_signer_designation: p.accounts_signer_designation || data.signer_designation,
        }));
      })
      .catch(() => {});
    setShowSignatureModal(true);
  };

  // iter198 — open a live SIGNED / PENDING / OPTIONAL preview modal.
  const openSignaturePreview = async () => {
    if (!selectedExit) return;
    try {
      const res = await fetch(`${API}/api/franchise-exit/signatures/preview/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      setSignaturePreview(data);
      setShowSignaturePreview(true);
    } catch (e) {
      toast.error('Failed to load signature preview');
    }
  };

  // Add Signatures (all sections at once)
  const handleAddSignature = async () => {
    if (!selectedExit) return;
    
    const { sandeep_selected, jayanti_selected, exit_manager_name } = signatureForm;
    
    if (!sandeep_selected && !jayanti_selected) {
      toast.error('Please select at least one Franchisor signatory');
      return;
    }
    if (!exit_manager_name.trim()) {
      toast.error('Please enter Exit Manager name');
      return;
    }
    if (franchiseDirectors.length === 0) {
      toast.error('No directors found for this franchise');
      return;
    }

    setLoading(true);
    try {
      // 1. Save Franchisor Signatories
      const selectedSignatories = [];
      if (sandeep_selected) selectedSignatories.push('Sandeep Gadhwal');
      if (jayanti_selected) selectedSignatories.push('Jayanti Kathale');
      
      await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signer_type: 'franchisor_signatories',
          selected_signatories: selectedSignatories
        })
      });

      // 1b. Accounts signatory (Shashikant Pande) — optional but recommended.
      if (signatureForm.accounts_selected) {
        await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token, signer_type: 'accounts_signatory',
            signer_name: signatureForm.accounts_signer_name,
            signer_designation: signatureForm.accounts_signer_designation,
          })
        });
      }

      // 2. Save Exit Manager
      await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signer_type: 'exit_manager',
          signer_name: exit_manager_name.trim(),
          signer_designation: signatureForm.exit_manager_designation
        })
      });

      // 3. Save Franchisee Directors
      await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signer_type: 'franchisee_directors',
          directors: franchiseDirectors
        })
      });

      // 4. Optional: Lease / Property Owner
      if (signatureForm.lease_owner_signer_name.trim()) {
        await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token, signer_type: 'lease_owner',
            signer_name: signatureForm.lease_owner_signer_name.trim(),
            capacity: signatureForm.lease_owner_capacity,
            mobile: signatureForm.lease_owner_mobile,
            email: signatureForm.lease_owner_email,
            signed: signatureForm.lease_owner_signed,
          })
        });
      }

      // 5. Optional: Franchisee Consultants (multi)
      const validConsultants = signatureForm.consultants.filter(c => c.signer_name.trim());
      if (validConsultants.length > 0) {
        await fetch(`${API}/api/franchise-exit/sign/${selectedExit.exit_id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token, signer_type: 'franchisee_consultants',
            consultants: validConsultants,
          })
        });
      }

      toast.success('All signatures recorded successfully');
      setShowSignatureModal(false);
      fetchExitDetails(selectedExit.exit_id);
    } catch (error) {
      toast.error('Failed to record signatures');
    } finally {
      setLoading(false);
    }
  };

  // Update Compliance
  const handleUpdateCompliance = async () => {
    if (!selectedExit) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/update-compliance/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...complianceForm })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Compliance checklist updated');
        setShowComplianceModal(false);
        fetchExitDetails(selectedExit.exit_id);
      } else {
        toast.error(data.detail || 'Failed to update compliance');
      }
    } catch (error) {
      toast.error('Failed to update compliance');
    } finally {
      setLoading(false);
    }
  };

  // Complete Exit
  const handleCompleteExit = async () => {
    if (!selectedExit) return;
    
    if (!window.confirm('Are you sure you want to finalize this exit? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/complete/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Exit process completed successfully');
        fetchExitDetails(selectedExit.exit_id);
        fetchExits();
      } else {
        toast.error(data.detail || 'Failed to complete exit');
      }
    } catch (error) {
      toast.error('Failed to complete exit');
    } finally {
      setLoading(false);
    }
  };

  // Cancel Exit
  const handleCancelExit = async () => {
    if (!selectedExit) return;
    
    const reason = window.prompt('Please enter cancellation reason:');
    if (!reason) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-exit/cancel/${selectedExit.exit_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, cancellation_reason: reason })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Exit process cancelled');
        fetchExitDetails(selectedExit.exit_id);
        fetchExits();
      } else {
        toast.error(data.detail || 'Failed to cancel exit');
      }
    } catch (error) {
      toast.error('Failed to cancel exit');
    } finally {
      setLoading(false);
    }
  };

  // Fetch exit details
  const fetchExitDetails = async (exitId) => {
    try {
      const res = await fetch(`${API}/api/franchise-exit/get/${exitId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        setSelectedExit(data.exit);
        // Populate forms with existing data
        if (data.exit.asset_handover) {
          setAssetForm(data.exit.asset_handover);
        }
        if (data.exit.financial_settlement) {
          setFinanceForm(data.exit.financial_settlement);
        }
        if (data.exit.compliance_checklist) {
          setComplianceForm(data.exit.compliance_checklist);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch exit details');
    }
  };

  // Download document
  const downloadDocument = async (docType) => {
    if (!selectedExit) return;
    
    const endpoints = {
      agreement: 'generate-exit-agreement',
      handover: 'generate-handover-report',
      settlement: 'generate-settlement-sheet',
      certificate: 'generate-exit-certificate'
    };
    
    const filenames = {
      agreement: 'Exit_Agreement',
      handover: 'Handover_Report',
      settlement: 'Settlement_Sheet',
      certificate: 'Exit_Certificate'
    };
    
    try {
      toast.info(`Generating ${docType}...`);
      // iter235 — cache-bust query so browsers/PDF viewers never serve
      // a stale copy of the previous legal wording (FR-007 P0 rewrite).
      const _bust = `?ts=${Date.now()}`;
      const res = await fetch(`${API}/api/franchise-exit/${endpoints[docType]}/${selectedExit.exit_id}${_bust}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store',
        body: JSON.stringify({ token })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to generate document');
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // iter235 — unique filename per click so the OS never overwrites
      // (and PDF viewers never silently reopen) an older cached copy.
      const _ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
      a.download = `${filenames[docType]}_${selectedExit.exit_id}_${_ts}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download document');
    }
  };

  // Add asset item
  const addAssetItem = (category) => {
    setAssetForm(prev => ({
      ...prev,
      [category]: [...prev[category], { name: '', quantity: '', condition: 'Good', remarks: '' }]
    }));
  };

  // Remove asset item
  const removeAssetItem = (category, index) => {
    setAssetForm(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  // Update asset item
  const updateAssetItem = (category, index, field, value) => {
    setAssetForm(prev => ({
      ...prev,
      [category]: prev[category].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Calculate net settlement
  const calculateNetSettlement = () => {
    const balance = parseFloat(financeForm.working_capital_balance) || 0;
    const deductions = 
      (parseFloat(financeForm.staff_salary_current) || 0) +
      (parseFloat(financeForm.shop_rental_current) || 0) +
      (parseFloat(financeForm.vendor_payments) || 0) +
      (parseFloat(financeForm.utility_bills) || 0) +
      (parseFloat(financeForm.other_dues) || 0);
    return balance - deductions;
  };

  // Get step status
  const getStepStatus = (stepKey) => {
    if (!selectedExit?.steps_completed) return false;
    return selectedExit.steps_completed[stepKey];
  };

  // Render exit list
  const renderExitList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          {isFranchiseOwner ? "Your Exit Records" : "Exit Records"}
        </h2>
        {!isFranchiseOwner && (
          <Button onClick={() => setShowInitiateModal(true)} data-testid="initiate-exit-btn">
            <Plus className="w-4 h-4 mr-2" />
            Initiate Exit
          </Button>
        )}
      </div>
      
      {exits.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">
              {isFranchiseOwner ? "No exit process has been initiated for your franchise" : "No exit records found"}
            </p>
            {!isFranchiseOwner && (
              <p className="text-sm text-gray-400 mt-1">Click &quot;Initiate Exit&quot; to start a new exit process</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {exits.map(exit => (
            <Card 
              key={exit.exit_id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                fetchExitDetails(exit.exit_id);
                setActiveTab('details');
              }}
              data-testid={`exit-card-${exit.exit_id}`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{exit.franchise_code}</h3>
                      <Badge className={STATUS_COLORS[exit.status] || 'bg-gray-100'}>
                        {exit.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{exit.franchise_name}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Exit ID: {exit.exit_id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Effective Date</p>
                    <p className="font-medium">{exit.effective_date}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {EXIT_REASONS.find(r => r.value === exit.exit_reason)?.label || exit.exit_reason}
                    </p>
                  </div>
                </div>
                
                {/* Progress indicators */}
                <div className="mt-4 flex gap-1">
                  {['exit_agreement', 'asset_handover', 'financial_settlement', 'compliance_confirmation', 'exit_certificate'].map(step => (
                    <div 
                      key={step}
                      className={`flex-1 h-2 rounded ${exit.steps_completed?.[step] ? 'bg-green-500' : 'bg-gray-200'}`}
                      title={step.replace('_', ' ')}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render exit details
  const renderExitDetails = () => {
    if (!selectedExit) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Select an exit record to view details</p>
          <Button variant="outline" className="mt-4" onClick={() => setActiveTab('list')}>
            Back to List
          </Button>
        </div>
      );
    }

    const isCompleted = selectedExit.status === 'completed';
    const isCancelled = selectedExit.status === 'cancelled';
    const canEdit = !isCompleted && !isCancelled && !isFranchiseOwner;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setActiveTab('list')}>
                ← Back
              </Button>
              <h2 className="text-xl font-semibold">{selectedExit.franchise_code}</h2>
              <Badge className={STATUS_COLORS[selectedExit.status] || 'bg-gray-100'}>
                {selectedExit.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-gray-600 ml-16">{selectedExit.franchise_name}</p>
            <p className="text-sm text-gray-400 ml-16">{selectedExit.legal_entity_name}</p>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelExit} className="text-red-600">
                <X className="w-4 h-4 mr-2" />
                Cancel Exit
              </Button>
              <Button onClick={handleCompleteExit} disabled={!Object.values(selectedExit.steps_completed || {}).slice(0, 4).every(Boolean)}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete Exit
              </Button>
            </div>
          )}
        </div>

        {/* Exit Info */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg">Exit Information</CardTitle>
            {session?.is_super_admin && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500 text-amber-800 hover:bg-amber-50"
                  onClick={openCriticalDatesModal}
                  data-testid="btn-edit-critical-dates"
                >
                  Edit Critical Dates
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-indigo-500 text-indigo-800 hover:bg-indigo-50"
                  onClick={openLegalFieldsModal}
                  data-testid="btn-edit-legal-fields"
                >
                  SA Legal Fields
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-500">Exit ID</Label>
                <p className="font-mono text-sm">{selectedExit.exit_id}</p>
              </div>
              <div>
                <Label className="text-gray-500">Initiated By</Label>
                <p className="capitalize">{selectedExit.initiated_by}</p>
              </div>
              <div>
                <Label className="text-gray-500">Reason</Label>
                <p>{EXIT_REASONS.find(r => r.value === selectedExit.exit_reason)?.label || selectedExit.exit_reason}</p>
              </div>
              <div>
                <Label className="text-gray-500">Status</Label>
                <p className="capitalize">{selectedExit.status?.replace(/_/g, ' ')}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-t pt-3" data-testid="critical-dates-panel">
              <div>
                <Label className="text-gray-500">Franchise Agreement Date</Label>
                <p className="font-medium" data-testid="cd-agreement">{criticalDates?.franchise_agreement_date ? String(criticalDates.franchise_agreement_date).slice(0,10) : '—'}</p>
                {criticalDates?.franchise_agreement_date_source === 'SA_CORRECTION' || criticalDates?.franchise_agreement_date_source === 'SA_CORRECTION_VIA_EXIT' ? (
                  <p className="text-[10px] text-amber-700 mt-0.5">SA corrected</p>
                ) : null}
              </div>
              <div>
                <Label className="text-gray-500">Effective Exit Date</Label>
                <p className="font-medium" data-testid="cd-effective">{criticalDates?.effective_exit_date ? String(criticalDates.effective_exit_date).slice(0,10) : '—'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Exit Month</Label>
                <p className="font-medium" data-testid="cd-exit-month">{criticalDates?.exit_month_label || '—'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Settlement Month</Label>
                <p className="font-medium" data-testid="cd-settlement-month">{criticalDates?.settlement_month_label || '—'}</p>
              </div>
            </div>
            {selectedExit.exit_reason_details && (
              <div className="mt-4">
                <Label className="text-gray-500">Details</Label>
                <p className="text-sm">{selectedExit.exit_reason_details}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter185 — Certificate readiness banner */}
        <CertificateReadinessBanner
          exitId={selectedExit.exit_id}
          token={token}
          isSA={!!session?.is_super_admin}
          refreshKey={selectedExit.updated_at || selectedExit.asset_handover_finalized_at || selectedExit.asset_handover_reopened_at}
          onEligibilityChange={(ok) => setCertEligible(!!ok)}
          onOpenChecklists={() => setShowChecklists(true)}
          onOpenSettlement={() => setShowSettlementV2(true)}
        />

        {/* FR-007 P0 — SA Diagnostic toggle (hidden until clicked) */}
        {session?.is_super_admin && (
          <div className="-mt-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-slate-600 hover:text-slate-900"
              onClick={() => { loadExitStatus(selectedExit.exit_id); setShowExitDiagnostic(true); }}
              data-testid="btn-toggle-exit-diagnostic"
            >
              <Eye className="w-3 h-3 mr-1" /> Exit Readiness Diagnostic (SA)
            </Button>
          </div>
        )}

        {/* Progress Steps */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Step 1: Exit Agreement */}
          <Card className={getStepStatus('exit_agreement') ? 'border-green-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileSignature className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Exit Agreement</span>
                {getStepStatus('exit_agreement') && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
              <p className="text-xs text-gray-500 mb-3">Signatures from both parties</p>
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full"
                onClick={() => openSignatureModal()}
                disabled={!canEdit}
              >
                {canEdit ? 'Add Signature' : 'View'}
              </Button>
              {(getStepStatus('exit_agreement') || selectedExit.signatures?.franchisor || selectedExit.signatures?.franchisee) && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="w-full mt-1"
                  onClick={() => downloadDocument('agreement')}
                >
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Asset Handover */}
          <Card className={getStepStatus('asset_handover') ? 'border-green-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-orange-600" />
                <span className="font-medium">Asset Handover</span>
                {getStepStatus('asset_handover') && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
              <p className="text-xs text-gray-500 mb-3">Physical assets checklist</p>
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full"
                onClick={() => setShowAssetModal(true)}
                disabled={!canEdit}
              >
                {canEdit ? 'Record Assets' : 'View'}
              </Button>
              {getStepStatus('asset_handover') && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="w-full mt-1"
                  onClick={() => downloadDocument('handover')}
                >
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              )}
              {getStepStatus('asset_handover') && session?.is_super_admin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-1 border-amber-500 text-amber-800 hover:bg-amber-50"
                  onClick={() => { setReopenReason(''); setShowReopenModal(true); }}
                  data-testid="btn-reopen-asset-handover"
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Reopen / Rebuild Handover
                </Button>
              )}
              {getStepStatus('asset_handover') && session?.is_super_admin && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full mt-1 text-slate-600"
                  onClick={() => { loadAHHistory(); setShowAHHistory(true); }}
                  data-testid="btn-view-handover-history"
                >
                  <FileText className="w-3 h-3 mr-1" /> View Repair History
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Financial Settlement */}
          <Card className={getStepStatus('financial_settlement') ? 'border-green-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-5 h-5 text-green-600" />
                <span className="font-medium">Settlement</span>
                {getStepStatus('financial_settlement') && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
              <p className="text-xs text-gray-500 mb-3">Financial reconciliation</p>
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full"
                onClick={() => setShowSettlementV2(true)}
                disabled={!canEdit}
                data-testid="btn-open-settlement-v2"
              >
                {canEdit ? 'Enter Details' : 'View'}
              </Button>
              {getStepStatus('financial_settlement') && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="w-full mt-1"
                  onClick={() => downloadDocument('settlement')}
                >
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              )}
              {getStepStatus('financial_settlement') && session?.is_super_admin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-1 border-amber-500 text-amber-800 hover:bg-amber-50"
                  onClick={() => { setRepairReason(''); setRepairResult(null); setShowRepairModal(true); }}
                  data-testid="btn-repair-settlement"
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh / Repair Settlement Data
                </Button>
              )}
              {getStepStatus('financial_settlement') && session?.is_super_admin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-1 border-red-500 text-red-700 hover:bg-red-50"
                  onClick={() => { setUnlockReason(''); setUnlockConfirmed(false); setShowUnlockModal(true); }}
                  data-testid="btn-unlock-settlement"
                >
                  <Unlock className="w-3 h-3 mr-1" /> Unlock Settlement
                </Button>
              )}
              {selectedExit.settlement_correction_in_progress && session?.is_super_admin && (
                <div className="mt-1 space-y-1">
                  <div className="text-[10px] text-amber-800 font-semibold px-1 py-0.5 border border-amber-400 bg-amber-50 rounded text-center">
                    ⚠ Settlement Correction Open
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleFreezeSettlement}
                    disabled={freezing}
                    data-testid="btn-freeze-settlement"
                  >
                    {freezing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Lock className="w-3 h-3 mr-1" />}
                    Freeze Final Settlement
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 4: Compliance */}
          <Card className={getStepStatus('compliance_confirmation') ? 'border-green-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
                <span className="font-medium">Compliance</span>
                {getStepStatus('compliance_confirmation') && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
              <p className="text-xs text-gray-500 mb-3">Final checklist</p>
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full"
                onClick={() => setShowComplianceModal(true)}
                disabled={!canEdit}
              >
                {canEdit ? 'Update' : 'View'}
              </Button>
            </CardContent>
          </Card>

          {/* Step 5: Certificate */}
          <Card className={getStepStatus('exit_certificate') ? 'border-green-500' : (certEligible ? 'border-amber-400' : '')} data-testid="certificate-tile">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <span className="font-medium">Certificate</span>
                {getStepStatus('exit_certificate') && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
              <p className="text-xs text-gray-500 mb-3">Final exit certificate</p>
              {isCompleted ? (
                <Button 
                  size="sm" 
                  className="w-full"
                  onClick={() => downloadDocument('certificate')}
                  data-testid="btn-download-certificate"
                >
                  <Download className="w-3 h-3 mr-1" /> Download
                </Button>
              ) : certEligible ? (
                <Button
                  size="sm"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => downloadDocument('certificate')}
                  data-testid="btn-generate-certificate"
                >
                  <Award className="w-3 h-3 mr-1" /> Generate Final Exit Certificate
                </Button>
              ) : (
                <p className="text-xs text-gray-400 text-center">Complete all steps first</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* iter196 — Draft Preview per tile + Complete Exit Pack */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium">Draft Preview — Every Tile</div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Preview any section BEFORE Freeze / Handover / Finalise / Signature / Certificate. Incomplete sections visibly show PENDING or DATA MISSING — never hidden.
                </p>
              </div>
              <Button size="sm" variant="default" onClick={() => setShowCompletePack(true)} data-testid="btn-complete-exit-pack">
                Preview Complete Exit Pack
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5 text-[11px]">
              {[
                ['lease', 'Rental / Lease'],
                ['debrand', 'Brand De-branding'],
                ['social', 'Social Media'],
                ['digital', 'Digital Presence'],
                ['access', 'Access Removal'],
                ['confidentiality', 'Confidentiality'],
                ['compliance', 'Compliance'],
                ['open_matters', 'Open Matters'],
                ['vendor_payables', 'Vendor / Utility'],
                ['settlement', 'Settlement'],
                ['food_inventory', 'Food Inventory'],
                ['asset_handover', 'Asset Handover'],
              ].map(([k, lbl]) => (
                <Button
                  key={k}
                  size="sm"
                  variant="outline"
                  className="justify-start h-8 text-[11px]"
                  onClick={() => setPreviewSection(k)}
                  data-testid={`btn-preview-${k}`}
                >
                  <Eye className="w-3 h-3 mr-1" /> {lbl}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* iter185 — Exit Closure Checklists (lease · debrand · social · digital · access · confidentiality · compliance · open matters) */}
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">Exit Closure Checklists</div>
              <p className="text-xs text-gray-500 mt-0.5">
                Rental lease · Brand de-branding · Social media · Digital presence · Access removal · Confidential info · Compliance · Open matters. Required before final certificate can be issued.
              </p>
            </div>
            <Button size="sm" onClick={() => setShowChecklists(true)} data-testid="btn-open-checklists">
              Open Checklists
            </Button>
          </CardContent>
        </Card>

        {/* iter191 — Annexure H (Employee Salary Settlement) Preview */}
        <Card className="border-slate-200" data-testid="annexure-h-card">
          <CardContent className="p-4">
            <div className="font-medium">Annexure H — Employee Salary Settlement</div>
            <p className="text-xs text-gray-500 mt-0.5">
              Initially imported from Attendance + Payroll; once edited or finalised it is an independent exit settlement (Payroll changes never overwrite it). Salaries already in operating expenses are not deducted again in the settlement.
            </p>
            <AnnexureHEditor exitId={selectedExit.exit_id} token={token} isSuperAdmin={!!session?.is_super_admin} onPreview={() => setShowAnnexureH(true)} />
          </CardContent>
        </Card>

        {/* Signatures Summary */}
        {selectedExit.signatures && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Signatures</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    size="sm" variant="outline" 
                    onClick={handleMigrateSignatures} 
                    disabled={loading}
                    data-testid="refresh-signatures-btn"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    Refresh from Master Data
                  </Button>
                  {!isFranchiseOwner && (
                    <Button 
                      size="sm" variant="outline"
                      onClick={handleMigrateAllSignatures}
                      disabled={loading}
                      data-testid="migrate-all-signatures-btn"
                      className="text-xs"
                    >
                      Update All Exits
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* FRANCHISOR SIGNATORIES */}
              <div>
                <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide">Franchisor (Purnabramha)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(selectedExit.signatures.franchisor_signatories?.length > 0) ? (
                    selectedExit.signatures.franchisor_signatories.map((s, i) => (
                      <div key={i} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-sm">{s.signer_name}</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-6">{s.signer_designation}</p>
                        <p className="text-xs text-gray-400 ml-6">Signed: {new Date(s.signature_date).toLocaleDateString()}</p>
                      </div>
                    ))
                  ) : selectedExit.signatures.franchisor ? (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-sm">{selectedExit.signatures.franchisor.signer_name}</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-6">{selectedExit.signatures.franchisor.signer_designation}</p>
                      <p className="text-xs text-gray-400 ml-6">Signed: {new Date(selectedExit.signatures.franchisor.signature_date).toLocaleDateString()}</p>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-400">Pending</p>
                    </div>
                  )}
                </div>
              </div>

              {/* EXIT MANAGER */}
              <div>
                <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide">Exit Manager (Franchisor Side)</h4>
                {selectedExit.signatures.exit_manager ? (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">{selectedExit.signatures.exit_manager.signer_name}</span>
                    </div>
                    <p className="text-xs text-gray-500 ml-6">{selectedExit.signatures.exit_manager.signer_role || selectedExit.signatures.exit_manager.signer_designation}</p>
                    <p className="text-xs text-gray-400 ml-6">Signed: {new Date(selectedExit.signatures.exit_manager.signature_date).toLocaleDateString()}</p>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-400">Pending</p>
                  </div>
                )}
              </div>

              {/* FRANCHISEE DIRECTORS */}
              <div>
                <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide">Franchisee Directors</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(selectedExit.signatures.franchisee_directors?.length > 0) ? (
                    selectedExit.signatures.franchisee_directors.map((d, i) => (
                      <div key={i} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="w-4 h-4 text-amber-600" />
                          <span className="font-medium text-sm">{d.signer_name}</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-6">{d.signer_designation}</p>
                        <p className="text-xs text-gray-400 ml-6">Signed: {new Date(d.signature_date).toLocaleDateString()}</p>
                      </div>
                    ))
                  ) : selectedExit.signatures.franchisee ? (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-sm">{selectedExit.signatures.franchisee.signer_name}</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-6">{selectedExit.signatures.franchisee.signer_designation}</p>
                      <p className="text-xs text-gray-400 ml-6">Signed: {new Date(selectedExit.signatures.franchisee.signature_date).toLocaleDateString()}</p>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-400">Pending</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Summary */}
        {selectedExit.financial_settlement && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Financial Settlement Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-600">Working Capital</p>
                  <p className="text-lg font-semibold">Rs. {selectedExit.financial_settlement.working_capital_balance?.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm text-red-600">Total Deductions</p>
                  <p className="text-lg font-semibold">Rs. {(
                    (selectedExit.financial_settlement.staff_salary_current || 0) +
                    (selectedExit.financial_settlement.shop_rental_current || 0) +
                    (selectedExit.financial_settlement.vendor_payments || 0) +
                    (selectedExit.financial_settlement.utility_bills || 0) +
                    (selectedExit.financial_settlement.other_dues || 0)
                  ).toLocaleString()}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg col-span-2">
                  <p className="text-sm text-green-600">Net Settlement Amount</p>
                  <p className="text-2xl font-bold text-green-700">
                    Rs. {selectedExit.financial_settlement.total_settlement?.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="franchise-exit-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Franchise Exit & Closure</h1>
        <p className="text-gray-600">Manage franchise exit processes and generate legal documents</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list">Exit Records</TabsTrigger>
          <TabsTrigger value="details" disabled={!selectedExit}>Exit Details</TabsTrigger>
        </TabsList>
        
        <TabsContent value="list" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : renderExitList()}
        </TabsContent>
        
        <TabsContent value="details" className="mt-6">
          {renderExitDetails()}
        </TabsContent>
      </Tabs>

      {/* Initiate Exit Modal */}
      <Dialog open={showInitiateModal} onOpenChange={setShowInitiateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Initiate Franchise Exit</DialogTitle>
            <DialogDescription>Start the exit process for a franchise</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Franchise *</Label>
              <Select value={initiateForm.franchise_code} onValueChange={(v) => setInitiateForm(p => ({ ...p, franchise_code: v }))}>
                <SelectTrigger data-testid="franchise-select">
                  <SelectValue placeholder="Select franchise" />
                </SelectTrigger>
                <SelectContent>
                  {franchises.map(f => (
                    <SelectItem key={f.franchise_code} value={f.franchise_code}>
                      {f.franchise_code} - {f.franchise_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Exit Reason *</Label>
              <Select value={initiateForm.exit_reason} onValueChange={(v) => setInitiateForm(p => ({ ...p, exit_reason: v }))}>
                <SelectTrigger data-testid="reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {EXIT_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason Details</Label>
              <Textarea 
                placeholder="Additional details..."
                value={initiateForm.exit_reason_details}
                onChange={(e) => setInitiateForm(p => ({ ...p, exit_reason_details: e.target.value }))}
              />
            </div>
            <div>
              <Label>Effective Date *</Label>
              <Input 
                type="date"
                value={initiateForm.effective_date}
                onChange={(e) => setInitiateForm(p => ({ ...p, effective_date: e.target.value }))}
                data-testid="effective-date-input"
              />
            </div>
            <div>
              <Label>Initiated By</Label>
              <Select value={initiateForm.initiated_by} onValueChange={(v) => setInitiateForm(p => ({ ...p, initiated_by: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="franchisor">Franchisor</SelectItem>
                  <SelectItem value="franchisee">Franchisee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInitiateModal(false)}>Cancel</Button>
            <Button onClick={handleInitiateExit} disabled={loading} data-testid="confirm-initiate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Initiate Exit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Handover Modal — iter199 compact reconciliation */}
      <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
        <DialogContent className="max-w-[95vw] w-full max-h-[92vh] h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Asset Handover — Compact Reconciliation</DialogTitle>
            <DialogDescription className="text-xs">
              Auto-populated from Inventory &amp; Food Stock.  Use search / bulk actions to reconcile quickly.  Historical accounting is never modified.
            </DialogDescription>
          </DialogHeader>
          {selectedExit && (
            <AssetHandoverCompact
              key={selectedExit.exit_id}
              exitId={selectedExit.exit_id}
              token={token}
              session={session}
              onClose={() => setShowAssetModal(false)}
              onSaved={() => fetchExitDetails(selectedExit.exit_id)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Financial Settlement Modal */}
      <Dialog open={showFinanceModal} onOpenChange={setShowFinanceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Financial Settlement</DialogTitle>
            <DialogDescription>Enter all financial details for settlement calculation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <Label className="text-blue-700">Working Capital Balance (Credit)</Label>
              <Input 
                type="number"
                value={financeForm.working_capital_balance}
                onChange={(e) => setFinanceForm(p => ({ ...p, working_capital_balance: parseFloat(e.target.value) || 0 }))}
                className="mt-1"
              />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-red-600 mb-3">DEDUCTIONS</p>
              <div className="space-y-3">
                <div>
                  <Label>Staff Salary (Current Month)</Label>
                  <Input 
                    type="number"
                    value={financeForm.staff_salary_current}
                    onChange={(e) => setFinanceForm(p => ({ ...p, staff_salary_current: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Shop Rental (Current Month)</Label>
                  <Input 
                    type="number"
                    value={financeForm.shop_rental_current}
                    onChange={(e) => setFinanceForm(p => ({ ...p, shop_rental_current: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Vendor Payments</Label>
                  <Input 
                    type="number"
                    value={financeForm.vendor_payments}
                    onChange={(e) => setFinanceForm(p => ({ ...p, vendor_payments: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Utility Bills</Label>
                  <Input 
                    type="number"
                    value={financeForm.utility_bills}
                    onChange={(e) => setFinanceForm(p => ({ ...p, utility_bills: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Other Dues</Label>
                  <Input 
                    type="number"
                    value={financeForm.other_dues}
                    onChange={(e) => setFinanceForm(p => ({ ...p, other_dues: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600">Net Settlement Amount</p>
              <p className={`text-2xl font-bold ${calculateNetSettlement() >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                Rs. {calculateNetSettlement().toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {calculateNetSettlement() >= 0 ? 'Payable to Franchisee' : 'Owed by Franchisee'}
              </p>
            </div>
            <div>
              <Label>Settlement Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={financeForm.settlement_notes}
                onChange={(e) => setFinanceForm(p => ({ ...p, settlement_notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinanceModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateFinance} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Settlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Exit Agreement Signatures</DialogTitle>
            <DialogDescription>Record signatures from all parties for the exit agreement</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {/* Section 1: Franchisor Signatories */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                1. Franchisor Signatory (Select one or both)
              </h4>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox 
                  id="sandeep_sign"
                  checked={signatureForm.sandeep_selected}
                  onCheckedChange={(checked) => setSignatureForm(p => ({ ...p, sandeep_selected: checked }))}
                  data-testid="sandeep-checkbox"
                />
                <Label htmlFor="sandeep_sign" className="cursor-pointer flex-1">
                  <span className="font-medium">Sandeep Gadhwal</span>
                  <span className="text-xs text-gray-500 block">Director - Manaswini Foods Pvt. Ltd.</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox 
                  id="jayanti_sign"
                  checked={signatureForm.jayanti_selected}
                  onCheckedChange={(checked) => setSignatureForm(p => ({ ...p, jayanti_selected: checked }))}
                  data-testid="jayanti-checkbox"
                />
                <Label htmlFor="jayanti_sign" className="cursor-pointer flex-1">
                  <span className="font-medium">Jayanti Kathale</span>
                  <span className="text-xs text-gray-500 block">Director - Manaswini Foods Pvt. Ltd.</span>
                </Label>
              </div>
            </div>

            {/* Section 2: Exit Manager */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                2. Exit Manager (Franchisor Side)
              </h4>
              <div>
                <Label className="text-xs">Name *</Label>
                <Input 
                  placeholder="e.g., Anirudha Suryavanshi"
                  value={signatureForm.exit_manager_name}
                  onChange={(e) => setSignatureForm(p => ({ ...p, exit_manager_name: e.target.value }))}
                  data-testid="exit-manager-name"
                />
              </div>
              <div>
                <Label className="text-xs">Designation</Label>
                <Input 
                  placeholder="Exit Manager"
                  value={signatureForm.exit_manager_designation}
                  onChange={(e) => setSignatureForm(p => ({ ...p, exit_manager_designation: e.target.value }))}
                  data-testid="exit-manager-designation"
                />
              </div>
            </div>

            {/* Section 3: Franchisee Directors (auto-pulled) */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                3. Franchisee Directors (Auto-populated from Franchise Management)
              </h4>
              {franchiseDirectors.length > 0 ? (
                <div className="space-y-2">
                  {franchiseDirectors.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{d.name}</p>
                        <p className="text-xs text-gray-500">{d.designation || 'Director'} {d.email ? `| ${d.email}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">All directors will be signed automatically</p>
                </div>
              ) : (
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-sm text-red-600">No directors found in Franchise Management</p>
                  <p className="text-xs text-gray-500 mt-1">Please add directors in Franchise Management first</p>
                </div>
              )}
            </div>

            {/* Section 1b: Accounts Signatory (iter198 — Shashikant Pande) */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                4. Authorised Accounts Signatory (pulled from Accounts config)
              </h4>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Checkbox
                  id="accounts_sign"
                  checked={signatureForm.accounts_selected}
                  onCheckedChange={(checked) => setSignatureForm(p => ({ ...p, accounts_selected: checked }))}
                  data-testid="accounts-signatory-checkbox"
                />
                <Label htmlFor="accounts_sign" className="cursor-pointer flex-1">
                  <span className="font-medium">{signatureForm.accounts_signer_name || accountsSignatoryConfig?.signer_name || 'Shashikant Pande'}</span>
                  <span className="text-xs text-gray-500 block">{signatureForm.accounts_signer_designation || accountsSignatoryConfig?.signer_designation || 'CFO (Head of Accounts)'} — Manaswini Foods Pvt. Ltd.</span>
                  <span className="text-[10px] text-blue-600 block">Same signatory as on every PIB / Accounts / GST report</span>
                </Label>
              </div>
            </div>

            {/* Section 5: OPTIONAL Lease / Property Owner */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                5. Lease / Property Owner <span className="text-[10px] normal-case text-gray-400 font-normal">(optional — only if lease is executed jointly)</span>
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input placeholder="Property Owner name"
                    value={signatureForm.lease_owner_signer_name}
                    onChange={(e) => setSignatureForm(p => ({ ...p, lease_owner_signer_name: e.target.value }))}
                    data-testid="lease-owner-name" />
                </div>
                <div>
                  <Label className="text-xs">Role / Capacity</Label>
                  <Input placeholder="e.g., Landlord / Owner"
                    value={signatureForm.lease_owner_capacity}
                    onChange={(e) => setSignatureForm(p => ({ ...p, lease_owner_capacity: e.target.value }))}
                    data-testid="lease-owner-capacity" />
                </div>
                <div>
                  <Label className="text-xs">Mobile</Label>
                  <Input placeholder="Optional"
                    value={signatureForm.lease_owner_mobile}
                    onChange={(e) => setSignatureForm(p => ({ ...p, lease_owner_mobile: e.target.value }))}
                    data-testid="lease-owner-mobile" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input placeholder="Optional"
                    value={signatureForm.lease_owner_email}
                    onChange={(e) => setSignatureForm(p => ({ ...p, lease_owner_email: e.target.value }))}
                    data-testid="lease-owner-email" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="lease_owner_signed"
                  checked={signatureForm.lease_owner_signed}
                  onCheckedChange={(v) => setSignatureForm(p => ({ ...p, lease_owner_signed: !!v }))}
                  data-testid="lease-owner-signed" />
                <Label htmlFor="lease_owner_signed" className="text-xs cursor-pointer">Lease owner has signed the agreement</Label>
              </div>
            </div>

            {/* Section 6: OPTIONAL Franchisee Consultants / Representatives (multi) */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide border-b pb-1">
                6. Franchisee Consultant / Representative <span className="text-[10px] normal-case text-gray-400 font-normal">(optional — CA / lawyer / advisor)</span>
              </h4>
              {signatureForm.consultants.map((c, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">Representative #{idx+1}</span>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-500"
                      onClick={() => setSignatureForm(p => ({ ...p, consultants: p.consultants.filter((_, i) => i !== idx) }))}
                      data-testid={`remove-consultant-${idx}`}>Remove</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Full Name" value={c.signer_name}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, signer_name: e.target.value } : x) }))}
                      data-testid={`consultant-name-${idx}`} />
                    <Input placeholder="Organisation / Firm" value={c.organisation}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, organisation: e.target.value } : x) }))} />
                    <Input placeholder="Designation / Capacity (e.g., CA, Advocate)" value={c.capacity}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, capacity: e.target.value } : x) }))} />
                    <Input placeholder="Representing (Franchisee / Both)" value={c.representing}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, representing: e.target.value } : x) }))} />
                    <Input placeholder="Mobile" value={c.mobile}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, mobile: e.target.value } : x) }))} />
                    <Input placeholder="Email" value={c.email}
                      onChange={(e) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, email: e.target.value } : x) }))} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={c.signed}
                      onCheckedChange={(v) => setSignatureForm(p => ({ ...p, consultants: p.consultants.map((x, i) => i === idx ? { ...x, signed: !!v } : x) }))} />
                    <Label className="text-xs cursor-pointer">Signed as consultant / representative / witness (NOT a contractual party)</Label>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setSignatureForm(p => ({ ...p, consultants: [...p.consultants, { signer_name: '', organisation: '', capacity: 'Consultant / Authorised Representative', representing: 'Franchisee', mobile: '', email: '', signed: false }] }))}
                data-testid="add-consultant-btn">+ Add Representative</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={openSignaturePreview} data-testid="preview-signatures-btn">
              <Eye className="w-4 h-4 mr-2" /> Preview Signatures
            </Button>
            <Button variant="outline" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
            <Button onClick={handleAddSignature} disabled={loading} data-testid="record-all-signatures-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
              Record All Signatures
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter198 — Signature Preview Modal (SIGNED / PENDING / OPTIONAL) */}
      <Dialog open={showSignaturePreview} onOpenChange={setShowSignaturePreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-600" /> Signature Page Preview
            </DialogTitle>
            <DialogDescription>
              Exactly what will render on the Exit Agreement PDF/Word.
              Optional signatories with no data are skipped entirely — no blank pages.
            </DialogDescription>
          </DialogHeader>
          {signaturePreview ? (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 text-sm">
              <SigPreviewBlock
                label="FOR FRANCHISOR — MANASWINI FOODS PVT LTD"
                entries={signaturePreview.franchisor_signatories}
                empty="No franchisor signatories selected yet"
              />
              <SigPreviewBlock
                label="Authorised Accounts Signatory"
                entries={[signaturePreview.accounts_signatory]}
                empty="Accounts signatory not selected"
              />
              <SigPreviewBlock
                label="Exit Manager (Franchisor Side)"
                entries={[signaturePreview.exit_manager]}
                empty="Exit manager not set"
              />
              <SigPreviewBlock
                label="FOR FRANCHISEE — Directors"
                entries={signaturePreview.franchisee_directors}
                empty="Franchisee directors not populated yet"
              />
              {signaturePreview.lease_owner?.signer_name ? (
                <SigPreviewBlock
                  label="Lease / Property Owner (OPTIONAL)"
                  entries={[signaturePreview.lease_owner]}
                  empty=""
                />
              ) : (
                <div className="p-3 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Lease / Property Owner</p>
                  <p className="text-xs text-slate-400 mt-1">OPTIONAL — Not Added · will NOT render on PDF/Word</p>
                </div>
              )}
              {signaturePreview.franchisee_consultants?.length > 0 ? (
                <SigPreviewBlock
                  label="Franchisee Consultant / Authorised Representative (OPTIONAL)"
                  entries={signaturePreview.franchisee_consultants}
                  empty=""
                  isConsultant
                />
              ) : (
                <div className="p-3 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Franchisee Consultant / Representative</p>
                  <p className="text-xs text-slate-400 mt-1">OPTIONAL — Not Added · will NOT render on PDF/Word</p>
                </div>
              )}
              <div className="mt-2 border-t pt-3 space-y-1">
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">Legend</p>
                <p className="text-[11px] text-slate-400"><span className="text-emerald-600 font-semibold">SIGNED</span> — signature captured & will render</p>
                <p className="text-[11px] text-slate-400"><span className="text-red-500 font-semibold">PENDING</span> — signatory added but not signed; PDF shows &ldquo;Signature Pending&rdquo;</p>
                <p className="text-[11px] text-slate-400"><span className="text-slate-500 font-semibold">OPTIONAL — NOT ADDED</span> — slot skipped entirely</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading preview…</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignaturePreview(false)} data-testid="close-signature-preview">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Compliance Modal */}
      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Compliance Confirmation</DialogTitle>
            <DialogDescription>Verify all compliance requirements before finalization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox 
                id="no_pending_payments"
                checked={complianceForm.no_pending_payments}
                onCheckedChange={(checked) => setComplianceForm(p => ({ ...p, no_pending_payments: checked }))}
              />
              <Label htmlFor="no_pending_payments" className="cursor-pointer">
                No pending payments to vendors, staff, or utilities
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox 
                id="brand_assets_transferred"
                checked={complianceForm.brand_assets_transferred}
                onCheckedChange={(checked) => setComplianceForm(p => ({ ...p, brand_assets_transferred: checked }))}
              />
              <Label htmlFor="brand_assets_transferred" className="cursor-pointer">
                Brand assets & digital properties returned to franchisor
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox 
                id="financial_report_signed"
                checked={complianceForm.financial_report_signed}
                onCheckedChange={(checked) => setComplianceForm(p => ({ ...p, financial_report_signed: checked }))}
              />
              <Label htmlFor="financial_report_signed" className="cursor-pointer">
                Financial settlement report signed by both parties
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox 
                id="handover_report_signed"
                checked={complianceForm.handover_report_signed}
                onCheckedChange={(checked) => setComplianceForm(p => ({ ...p, handover_report_signed: checked }))}
              />
              <Label htmlFor="handover_report_signed" className="cursor-pointer">
                Asset handover report signed by both parties
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCompliance} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Compliance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter185 — Settlement V2 & Closure Checklists modals */}
      {selectedExit && (
        <>
          <SettlementV2Modal
            open={showSettlementV2}
            onOpenChange={setShowSettlementV2}
            exitId={selectedExit.exit_id}
            token={token}
            onFinalized={() => fetchExitDetails(selectedExit.exit_id)}
          />
          <ExitChecklistsPanel
            open={showChecklists}
            onOpenChange={setShowChecklists}
            exitId={selectedExit.exit_id}
            token={token}
            isSA={!!session?.is_super_admin}
            isAccounts={['accounts', 'admin', 'super_admin'].includes((session?.role_key || '').toLowerCase())}
            onSectionsChanged={() => { fetchExitDetails(selectedExit.exit_id); loadExitStatus(selectedExit.exit_id); }}
          />
          <AnnexureHPreviewModal
            open={showAnnexureH}
            onOpenChange={setShowAnnexureH}
            exitId={selectedExit.exit_id}
            token={token}
          />
          <DraftPreviewModal
            open={!!previewSection}
            onOpenChange={(v) => !v && setPreviewSection(null)}
            exitId={selectedExit.exit_id}
            token={token}
            section={previewSection}
            isSA={!!session?.is_super_admin}
            onRefresh={() => fetchExitDetails(selectedExit.exit_id)}
          />
          <CompleteExitPackPreviewModal
            open={showCompletePack}
            onOpenChange={setShowCompletePack}
            exitId={selectedExit.exit_id}
            token={token}
          />

          {/* FR-007 P0 — Critical Dates Editor (SA only) */}
          <Dialog open={showCriticalDatesModal} onOpenChange={setShowCriticalDatesModal}>
            <DialogContent className="max-w-lg" data-testid="critical-dates-modal">
              <DialogHeader>
                <DialogTitle>Edit Critical Dates</DialogTitle>
                <DialogDescription className="text-xs">
                  These are two DIFFERENT legal dates and never overwrite each other. Editing here does NOT reset Asset Handover, Settlement, Compliance, Signatures, or Annexures — it recalculates the certificate readiness only.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Franchise Agreement Date</Label>
                  <Input type="date" value={cdForm.franchise_agreement_date} onChange={(e) => setCdForm({ ...cdForm, franchise_agreement_date: e.target.value })} data-testid="input-cd-agreement" />
                  <p className="text-[10px] text-slate-500 mt-1">Pulled from Franchise Master. Leave blank to keep the existing value.</p>
                </div>
                <div>
                  <Label className="text-xs">Effective Exit Date</Label>
                  <Input type="date" value={cdForm.effective_exit_date} onChange={(e) => setCdForm({ ...cdForm, effective_exit_date: e.target.value })} data-testid="input-cd-effective" />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 border rounded p-2">
                  <div>
                    <Label className="text-slate-500">Exit Month (auto)</Label>
                    <p className="font-medium" data-testid="cd-derived-exit-month">{cdForm.effective_exit_date ? new Date(cdForm.effective_exit_date + 'T00:00:00').toLocaleString('en-US', { month: 'long', year: 'numeric' }) : '—'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Settlement Month (auto)</Label>
                    <p className="font-medium" data-testid="cd-derived-settlement-month">{cdForm.effective_exit_date ? new Date(cdForm.effective_exit_date + 'T00:00:00').toLocaleString('en-US', { month: 'long', year: 'numeric' }) : '—'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Reason for Change <span className="text-red-500">*</span></Label>
                  <Input value={cdForm.reason} onChange={(e) => setCdForm({ ...cdForm, reason: e.target.value })} placeholder="Audit trail — mandatory" data-testid="input-cd-reason" />
                </div>
                <div>
                  <Label className="text-xs">Remarks</Label>
                  <Textarea rows={2} value={cdForm.remarks} onChange={(e) => setCdForm({ ...cdForm, remarks: e.target.value })} data-testid="input-cd-remarks" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCriticalDatesModal(false)}>Cancel</Button>
                <Button onClick={saveCriticalDates} disabled={savingCritical} data-testid="btn-save-critical-dates">
                  {savingCritical && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* iter242 — SA Legal Fields Modal (6 legal override fields) */}
          <Dialog open={showLegalFieldsModal} onOpenChange={setShowLegalFieldsModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="legal-fields-modal">
              <DialogHeader>
                <DialogTitle>SA Legal Fields — Executed Agreement Overrides</DialogTitle>
                <DialogDescription className="text-xs">
                  These six fields are read by the Exit Agreement + Certificate PDF generators (Annexure E — Landlord / Security Deposit / Physical Handover) plus the Financial Reconciliation clause. Editing here NEVER touches signatures, snapshots, or the finalized_at flags. Mandatory reason recorded on the audit trail.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* 1 — Landlord Release Status */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Landlord Release Status</Label>
                    <Select value={lfForm.landlord_release_status || 'UNCHANGED'} onValueChange={v => setLfForm({ ...lfForm, landlord_release_status: v === 'UNCHANGED' ? '' : v })}>
                      <SelectTrigger data-testid="lf-landlord-release-status"><SelectValue placeholder="Keep existing" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNCHANGED">-- Keep existing --</SelectItem>
                        <SelectItem value="PENDING">PENDING</SelectItem>
                        <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                        <SelectItem value="RESERVED">RESERVED</SelectItem>
                        <SelectItem value="NOT_APPLICABLE">NOT_APPLICABLE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Landlord Release Notes</Label>
                    <Input value={lfForm.landlord_release_notes} onChange={e => setLfForm({ ...lfForm, landlord_release_notes: e.target.value })} placeholder="Free text — appears in Annexure E" data-testid="lf-landlord-release-notes" />
                  </div>
                </div>

                {/* 2 — Security Deposit Status */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Security Deposit Status</Label>
                    <Select value={lfForm.security_deposit_status || 'UNCHANGED'} onValueChange={v => setLfForm({ ...lfForm, security_deposit_status: v === 'UNCHANGED' ? '' : v })}>
                      <SelectTrigger data-testid="lf-security-deposit-status"><SelectValue placeholder="Keep existing" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNCHANGED">-- Keep existing --</SelectItem>
                        <SelectItem value="PENDING">PENDING</SelectItem>
                        <SelectItem value="HANDLED_BY_FRANCHISEE">HANDLED_BY_FRANCHISEE</SelectItem>
                        <SelectItem value="NOT_RECEIVED_MFPL">NOT_RECEIVED_MFPL</SelectItem>
                        <SelectItem value="RECEIVED">RECEIVED</SelectItem>
                        <SelectItem value="NOT_APPLICABLE">NOT_APPLICABLE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Security Deposit Notes</Label>
                    <Input value={lfForm.security_deposit_notes} onChange={e => setLfForm({ ...lfForm, security_deposit_notes: e.target.value })} placeholder="Free text — appears in Annexure E" data-testid="lf-security-deposit-notes" />
                  </div>
                </div>

                {/* 3 — Landlord Inspection */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Landlord Inspection Status</Label>
                    <Select value={lfForm.landlord_inspection_status || 'UNCHANGED'} onValueChange={v => setLfForm({ ...lfForm, landlord_inspection_status: v === 'UNCHANGED' ? '' : v })}>
                      <SelectTrigger data-testid="lf-landlord-inspection-status"><SelectValue placeholder="Keep existing" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNCHANGED">-- Keep existing --</SelectItem>
                        <SelectItem value="PENDING">PENDING</SelectItem>
                        <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                        <SelectItem value="NOT_APPLICABLE">NOT_APPLICABLE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Handover Exceptions Note</Label>
                    <Input value={lfForm.handover_exceptions_note} onChange={e => setLfForm({ ...lfForm, handover_exceptions_note: e.target.value })} placeholder="e.g. NIL, or list of specific exceptions" data-testid="lf-handover-exceptions-note" />
                  </div>
                </div>

                {/* Salary Payment (optional financial fields) */}
                <div className="grid grid-cols-2 gap-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">Final Salary Paid At (optional)</Label>
                    <Input type="date" value={lfForm.salary_paid_at} onChange={e => setLfForm({ ...lfForm, salary_paid_at: e.target.value })} data-testid="lf-salary-paid-at" />
                  </div>
                  <div>
                    <Label className="text-xs">Final Salary Amount (optional)</Label>
                    <Input type="number" value={lfForm.salary_amount} onChange={e => setLfForm({ ...lfForm, salary_amount: e.target.value })} placeholder="0.00" data-testid="lf-salary-amount" />
                  </div>
                </div>

                {/* Reason — mandatory */}
                <div className="border-t pt-3">
                  <Label className="text-xs">Reason for Change <span className="text-red-500">*</span></Label>
                  <Textarea rows={2} value={lfForm.reason} onChange={e => setLfForm({ ...lfForm, reason: e.target.value })} placeholder="Audit trail — mandatory" data-testid="lf-reason" />
                  <p className="text-[10px] text-slate-500 mt-1">Fields left blank / "Keep existing" are not touched.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowLegalFieldsModal(false)}>Cancel</Button>
                <Button onClick={saveLegalFields} disabled={savingLegal} data-testid="btn-save-legal-fields">
                  {savingLegal && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Save Legal Fields
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* FR-007 P0 — SA Exit Readiness Diagnostic (toggleable) */}
          {session?.is_super_admin && (
            <Dialog open={showExitDiagnostic} onOpenChange={setShowExitDiagnostic}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="exit-diagnostic-modal">
                <DialogHeader>
                  <DialogTitle>Exit Readiness Diagnostic</DialogTitle>
                  <DialogDescription className="text-xs">
                    Live status of every gate that controls the Final Exit Certificate. Blockers include their source (collection & record id) so any inconsistency can be traced instantly.
                  </DialogDescription>
                </DialogHeader>
                {exitStatusData ? (
                  <div className="space-y-2 text-xs">
                    <div className={`p-2 rounded border ${exitStatusData.can_issue_certificate ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`} data-testid="diag-overall">
                      <b>Certificate:</b> {exitStatusData.can_issue_certificate ? 'READY' : `BLOCKED — ${(exitStatusData.blockers || []).length} item(s)`}
                    </div>
                    <div className="grid grid-cols-2 gap-2 border rounded p-2 bg-slate-50">
                      <div><b>Financial Settlement:</b> {exitStatusData.financial_settlement_finalized ? '✓ COMPLETED' : '✗ PENDING'}</div>
                      <div><b>Asset Handover:</b> {exitStatusData.asset_handover_finalized ? '✓ COMPLETED' : '✗ PENDING'}</div>
                    </div>
                    <div>
                      <b>Sections:</b>
                      <table className="w-full text-[11px] mt-1 border rounded">
                        <thead className="bg-slate-100"><tr>
                          <th className="px-2 py-1 text-left">Section</th>
                          <th className="px-2 py-1 text-center">Status</th>
                          <th className="px-2 py-1 text-left">Detail</th>
                        </tr></thead>
                        <tbody>
                          {(exitStatusData.sections || []).map((s) => (
                            <tr key={s.section} className="border-t">
                              <td className="px-2 py-0.5">{s.label || s.section}</td>
                              <td className="px-2 py-0.5 text-center">{s.completed ? '✓' : '✗'}</td>
                              <td className="px-2 py-0.5 text-slate-500">
                                {s.rag_effective ? `RAG: ${s.rag_effective}` : s.total ? `${s.done}/${s.total}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(exitStatusData.blockers || []).length > 0 && (
                      <div>
                        <b className="text-red-700">Blockers:</b>
                        <div className="space-y-1 mt-1">
                          {exitStatusData.blockers.map((b, i) => (
                            <div key={i} className="p-2 border rounded bg-red-50 border-red-200 text-[11px]">
                              <div className="font-semibold text-red-800">{b.label} — {b.severity}</div>
                              <div>{b.message}</div>
                              <div className="text-slate-600 mt-1">
                                <span className="mr-3">SOURCE: <code>{b.source_collection || '—'}</code></span>
                                {b.source_record_id && <span className="mr-3">RECORD: <code>{String(b.source_record_id).slice(0, 40)}</code></span>}
                                {b.incomplete_row_ids && b.incomplete_row_ids.length > 0 && <span>ROWS: <code>{b.incomplete_row_ids.slice(0, 5).join(', ')}</code></span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => { loadExitStatus(selectedExit.exit_id); }}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                  <Button onClick={() => setShowExitDiagnostic(false)}>Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* iter224 — Refresh / Repair Settlement Data (SA only) */}
          <Dialog open={showRepairModal} onOpenChange={(v) => { setShowRepairModal(v); if (!v) { setRepairReason(''); setRepairResult(null); } }}>
            <DialogContent className="max-w-2xl" data-testid="repair-settlement-modal">
              <DialogHeader>
                <DialogTitle className="text-amber-700">Refresh / Repair Settlement Data</DialogTitle>
                <DialogDescription className="text-xs text-slate-700 space-y-1">
                  <span className="block">
                    This settlement is <b>already FINALISED</b>. This is a controlled <b>data repair</b> of the existing finalised snapshot — the original finalisation event, timestamp and finaliser are preserved.
                  </span>
                  <span className="block">
                    ERP sources pulled: Working Capital, Sales / credits, Operating Expenses, Staff Salary, MGT Allocation, Rent, Vendor Outstanding, Utilities, Other Dues.  Duplicate-expense protection is applied automatically.
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Repair Reason (mandatory, audit-logged)</Label>
                  <Textarea
                    value={repairReason}
                    onChange={(e) => setRepairReason(e.target.value)}
                    rows={3}
                    placeholder="e.g., Post-finalisation ERP source reconciliation — WC / Salary / Rent were not populated correctly at first freeze."
                    data-testid="repair-reason"
                  />
                </div>
                {repairResult && (
                  <div className="p-2 border rounded bg-emerald-50 border-emerald-300 text-[11px] space-y-1" data-testid="repair-result">
                    <div><b>Data repaired:</b> {String(!!repairResult.data_repaired)}</div>
                    <div><b>Was already finalised:</b> {String(!!repairResult.was_finalized)}</div>
                    <div><b>Snapshot version:</b> {repairResult.previous_snapshot_version} → {repairResult.snapshot_version}</div>
                    {repairResult.original_finalised_by && (
                      <div><b>Original finalisation preserved:</b> {repairResult.original_finalised_by} on {(repairResult.original_finalised_at || '').slice(0, 19)}</div>
                    )}
                    <div className="mt-1">
                      <Button size="sm" variant="outline" onClick={() => downloadDocument('settlement')} data-testid="repair-download-pdf">
                        <Download className="w-3 h-3 mr-1" /> Download Repaired Settlement PDF
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowRepairModal(false); setRepairReason(''); setRepairResult(null); }}>Close</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleRepairSettlement}
                  disabled={repairing}
                  data-testid="repair-submit"
                >
                  {repairing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Refresh / Repair
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* iter225 — Reopen / Rebuild Asset Handover (SA only) */}
          <Dialog open={showReopenModal} onOpenChange={(v) => { setShowReopenModal(v); if (!v) setReopenReason(''); }}>
            <DialogContent className="max-w-xl" data-testid="reopen-handover-modal">
              <DialogHeader>
                <DialogTitle className="text-amber-700">Reopen / Rebuild Asset Handover</DialogTitle>
                <DialogDescription className="text-xs text-slate-700 space-y-1">
                  <span className="block">
                    The Asset Handover is <b>already FINALISED</b>.  Reopening supersedes the current version and lets you re-finalise after applying the new non-blocking price rule.
                  </span>
                  <span className="block">
                    Your previously accepted <b>physical quantities are preserved</b> — you do not need to re-enter any qty.  The prior version stays in the audit history.
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Reopen Reason (mandatory, audit-logged)</Label>
                  <Textarea
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    rows={3}
                    placeholder="e.g., Exit inventory pricing rule corrected — missing rate is non-blocking; physical quantity retained as source of truth."
                    data-testid="reopen-reason"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowReopenModal(false); setReopenReason(''); }}>Cancel</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleReopenHandover}
                  disabled={reopening}
                  data-testid="reopen-submit"
                >
                  {reopening ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Reopen Handover
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* iter229 — SA Unlock Final Settlement modal */}
          <Dialog open={showUnlockModal} onOpenChange={(v) => { setShowUnlockModal(v); if (!v) { setUnlockReason(''); setUnlockConfirmed(false); } }}>
            <DialogContent className="max-w-lg" data-testid="unlock-settlement-modal">
              <DialogHeader>
                <DialogTitle className="text-red-700">UNLOCK FINAL SETTLEMENT</DialogTitle>
                <DialogDescription className="text-xs text-slate-700">
                  Unlocking temporarily reopens the Financial Settlement for correction.  Exit Agreement, Asset Handover, Compliance, Annexure E, Annexure H and other completed sections are NOT touched.  The settlement must be frozen again after refresh.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Reason for Unlock <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.target.value)}
                    rows={3}
                    placeholder="e.g., PDF shows DATA PENDING despite screen showing real ERP figures — refresh + freeze."
                    data-testid="unlock-reason"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <Checkbox checked={unlockConfirmed} onCheckedChange={(v) => setUnlockConfirmed(!!v)} data-testid="unlock-confirm" />
                  <span>I understand that this will temporarily reopen the financial settlement for correction and the settlement must be frozen again.</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowUnlockModal(false); setUnlockReason(''); setUnlockConfirmed(false); }}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleUnlockSettlement}
                  disabled={unlocking}
                  data-testid="unlock-submit"
                >
                  {unlocking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Unlock className="w-3 h-3 mr-1" />}
                  Unlock Settlement
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* iter228 — Asset Handover Repair History drawer */}
          <Dialog open={showAHHistory} onOpenChange={setShowAHHistory}>            <DialogContent className="max-w-3xl" data-testid="handover-history-modal">
              <DialogHeader>
                <DialogTitle>Asset Handover — Repair History</DialogTitle>
                <DialogDescription className="text-xs text-slate-700">
                  Every superseded finalisation of this exit's Asset Handover, newest first.  Physical quantities are preserved across all versions.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[400px] overflow-y-auto">
                {ahHistory.length === 0 ? (
                  <p className="text-xs text-slate-500 py-6 text-center">No prior versions — this handover has been finalised only once.</p>
                ) : (
                  <div className="space-y-2" data-testid="handover-history-list">
                    {ahHistory.map((h, idx) => (
                      <div key={idx} className="border rounded p-3 bg-slate-50 text-xs" data-testid={`handover-history-row-${idx}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded-full border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-semibold">
                            v{h.version} · {h.status || 'SUPERSEDED'}
                          </span>
                          <span className="text-slate-500">Rows preserved: {h.row_count}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-y-0.5 gap-x-3">
                          <div><b>Originally finalised:</b> {h.finalized_by || '—'} on {(h.finalized_at || '').slice(0, 19) || '—'}</div>
                          <div><b>Reopened by:</b> {h.reopened_by || '—'} on {(h.reopened_at || '').slice(0, 19) || '—'}</div>
                        </div>
                        {h.reason && (
                          <div className="mt-1"><b>Reason:</b> {h.reason}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAHHistory(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// iter184 — Asset Handover Reconciliation panel
// Loads the frozen exit snapshot and lets the SA verify physical qty /
// condition / franchisee acceptance, add manual rows, delete rows and
// safely Refresh from Current Data.
// ══════════════════════════════════════════════════════════════════════
const EXIT_HEAD_META = {
  kitchen_equipment:   { label: 'Kitchen Equipment',           icon: '🍳' },
  furniture_fixtures:  { label: 'Furniture & Fixtures',        icon: '🪑' },
  utensils_machinery:  { label: 'Utensils & Machinery',        icon: '🔧' },
  service_electrical:  { label: 'Service & Electrical Equipment', icon: '⚡' },
  food_inventory:      { label: 'Food Inventory',              icon: '🍲' },
  packaging_materials: { label: 'Packaging Materials',         icon: '📦' },
  other_assets:        { label: 'Other Assets',                icon: '📋' },
  unclassified:        { label: 'Unclassified — Review Required', icon: '❓' },
};
const CONDITIONS = ['New', 'Good', 'Fair', 'Repair Required', 'Damaged', 'Missing', 'Scrap'];

function AssetHandoverReconciliation({ exitId, token, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [grouped, setGrouped] = useState({});
  const [totals, setTotals] = useState({ rows: 0, accepted: 0, physical_verified: 0, auto_rows: 0, manual_rows: 0, by_head: {} });
  const [headsOrdered, setHeadsOrdered] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const [rowDrafts, setRowDrafts] = useState({});           // per-row unsaved patches
  const [openHeads, setOpenHeads] = useState({});           // section collapse
  const [addForms, setAddForms] = useState({});             // per-head new-row draft

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/list/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.detail || 'Load failed');
      setGrouped(d.grouped || {});
      setTotals(d.totals || {});
      setHeadsOrdered(d.heads_ordered || Object.keys(d.grouped || {}));
      setLastEvent(d.last_snapshot_event || null);
      // auto-open every non-empty head
      setOpenHeads(prev => {
        const next = { ...prev };
        for (const h of (d.heads_ordered || [])) {
          if (next[h] === undefined) next[h] = ((d.grouped || {})[h] || []).length > 0;
        }
        return next;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load handover');
    } finally { setLoading(false); }
  }, [exitId, token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const patch = (sid, field, value) =>
    setRowDrafts(p => ({ ...p, [sid]: { ...(p[sid] || {}), [field]: value } }));

  const isDirty = (sid) => rowDrafts[sid] && Object.keys(rowDrafts[sid]).length > 0;

  const saveRow = async (row) => {
    const patchObj = rowDrafts[row.snapshot_item_id];
    if (!patchObj) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/upsert-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_id: row.snapshot_item_id, ...patchObj }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success('Row saved');
      setRowDrafts(p => { const n = { ...p }; delete n[row.snapshot_item_id]; return n; });
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  const deleteRow = async (row) => {
    const reason = window.prompt(`Delete "${row.asset_name}" from the handover?\nReason (recorded in audit):`, '');
    if (reason === null) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/delete-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_id: row.snapshot_item_id, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Delete failed');
      toast.success('Row removed');
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  const addManualRow = async (head) => {
    const draft = addForms[head] || {};
    if (!draft.asset_name) { toast.error('Item name is required'); return; }
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/upsert-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token, exit_head: head,
          asset_name: draft.asset_name, unit: draft.unit || '',
          quantity_system: draft.quantity_system ? Number(draft.quantity_system) : null,
          quantity_physical: draft.quantity_physical ? Number(draft.quantity_physical) : null,
          condition_physical: draft.condition_physical,
          remarks: draft.remarks || '',
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Add failed');
      toast.success('Manual row added (audited)');
      setAddForms(p => ({ ...p, [head]: {} }));
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  const doRefresh = async () => {
    const reason = window.prompt('Refresh from Current Data will add newly-created inventory items and refresh System Qty on existing rows. Your physical / condition / remarks / acceptance data will NOT be touched.\n\nOptional reason:', 'pre-handover refresh');
    if (reason === null) return;
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/refresh/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Refresh failed');
      toast.success(`Refreshed — ${d.added} new, ${d.refreshed} updated, ${d.unchanged} unchanged`);
      await fetchList();
    } catch (e) { toast.error(e.message); }
    finally { setRefreshing(false); }
  };

  const doFinalize = async (force = false) => {
    setFinalizing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/finalize/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, force }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Finalize failed');
      if (d.success === false) {
        if (window.confirm(`${d.message}\n\nProceed with force finalization?`)) {
          return doFinalize(true);
        }
        return;
      }
      toast.success('Asset handover finalized');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setFinalizing(false); }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-400 flex items-center justify-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading handover snapshot…
    </div>;
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b pb-3">
        <div className="text-sm">
          <span className="font-semibold">{totals.rows}</span> items ·
          <span className="ml-2"><span className="font-semibold text-emerald-600">{totals.auto_rows}</span> auto</span> ·
          <span className="ml-2"><span className="font-semibold text-blue-600">{totals.manual_rows}</span> manual</span> ·
          <span className="ml-2"><span className="font-semibold text-purple-600">{totals.physical_verified}</span> physically verified</span> ·
          <span className="ml-2"><span className="font-semibold text-amber-600">{totals.accepted}</span> franchisee-accepted</span>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={doRefresh} disabled={refreshing}
            data-testid="ah-refresh-btn">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Refresh from Current Data
          </Button>
          <Button size="sm" onClick={() => doFinalize(false)} disabled={finalizing}
            data-testid="ah-finalize-btn" className="bg-emerald-600 hover:bg-emerald-700">
            {finalizing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
            Finalize Handover
          </Button>
        </div>
      </div>
      {lastEvent && (
        <div className="text-[11px] text-slate-500 pb-1">
          Last {lastEvent.action === 'AUTO_SNAPSHOT_BUILT' ? 'auto-built' : 'refreshed'} at {new Date(lastEvent.timestamp).toLocaleString()} by {lastEvent.actor}
        </div>
      )}

      {/* Grouped tables */}
      <div className="flex-1 overflow-y-auto space-y-3 mt-2 pr-1">
        {headsOrdered.map((head) => {
          const rows = grouped[head] || [];
          const meta = EXIT_HEAD_META[head] || { label: head, icon: '📦' };
          const open = !!openHeads[head];
          return (
            <div key={head} className={`border rounded-lg ${head === 'unclassified' && rows.length > 0 ? 'border-amber-300 bg-amber-50/40' : ''}`}
              data-testid={`ah-section-${head}`}>
              <button type="button"
                onClick={() => setOpenHeads(p => ({ ...p, [head]: !p[head] }))}
                className="w-full flex items-center gap-2 p-3 text-left"
                data-testid={`ah-section-toggle-${head}`}>
                <span className="text-lg">{meta.icon}</span>
                <span className="font-medium">{meta.label}</span>
                <Badge variant="outline" className="ml-1 text-[11px]">{rows.length}</Badge>
                <span className="ml-auto text-[11px] text-slate-400">{open ? '▼' : '▶'}</span>
              </button>
              {open && (
                <div className="border-t px-3 pt-2 pb-3 space-y-2">
                  {rows.length === 0 && (
                    <p className="text-xs text-slate-400 italic px-1">No system-recorded items for this head — use &quot;+ Add&quot; for a physically-found item.</p>
                  )}
                  {rows.map((row) => {
                    const sid = row.snapshot_item_id;
                    const draft = rowDrafts[sid] || {};
                    const v = (f) => (draft[f] !== undefined ? draft[f] : row[f]);
                    return (
                      <div key={sid} data-testid={`ah-row-${sid}`}
                        className={`grid grid-cols-12 gap-2 items-center rounded border px-2 py-2 text-xs ${isDirty(sid) ? 'bg-yellow-50 border-yellow-300' : 'bg-white'}`}>
                        <div className="col-span-3">
                          <div className="font-medium text-slate-700 truncate flex items-center gap-1">
                            {row.asset_name}
                            {row.source_tag === 'MANUAL_EXIT_ENTRY' && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-100 text-blue-700 border-blue-200">MANUAL</Badge>
                            )}
                            {row.source_tag === 'AUTO_CONSUMPTION' && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-emerald-100 text-emerald-700 border-emerald-200">FOOD STOCK</Badge>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 truncate">
                            {row.subcategory || row.category_code || '—'}
                            {row.location ? ` · ${row.location}` : ''}
                          </div>
                        </div>
                        <div className="col-span-1 text-center text-slate-500">
                          <div>{row.quantity_system ?? '—'}</div>
                          <div className="text-[9px]">{row.unit || ''}</div>
                          <div className="text-[9px] text-slate-400">system</div>
                        </div>
                        <div className="col-span-1">
                          <Input type="number" step="0.001"
                            className="h-7 text-right text-xs"
                            placeholder="physical"
                            value={v('quantity_physical') ?? ''}
                            onChange={(e) => patch(sid, 'quantity_physical', e.target.value === '' ? null : Number(e.target.value))}
                            data-testid={`ah-qty-${sid}`} />
                        </div>
                        <div className="col-span-1 text-center">
                          {(() => {
                            const sq = Number(row.quantity_system || 0);
                            const pq = Number(v('quantity_physical') ?? NaN);
                            if (!Number.isFinite(pq)) return <span className="text-slate-300">—</span>;
                            const diff = pq - sq;
                            const tone = diff === 0 ? 'text-emerald-600' : (diff > 0 ? 'text-blue-600' : 'text-red-600');
                            return <span className={`font-medium ${tone}`}>{diff > 0 ? `+${diff}` : diff}</span>;
                          })()}
                        </div>
                        <div className="col-span-2">
                          <Select value={v('condition_physical') || ''}
                            onValueChange={(val) => patch(sid, 'condition_physical', val)}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`ah-cond-${sid}`}>
                              <SelectValue placeholder={row.condition_system || 'condition'} />
                            </SelectTrigger>
                            <SelectContent>
                              {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input placeholder="remarks"
                            className="h-7 text-xs"
                            value={v('remarks') || ''}
                            onChange={(e) => patch(sid, 'remarks', e.target.value)}
                            data-testid={`ah-remarks-${sid}`} />
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <Checkbox
                              checked={!!v('accepted_by_franchisee')}
                              onCheckedChange={(c) => patch(sid, 'accepted_by_franchisee', !!c)}
                              data-testid={`ah-accept-${sid}`} />
                            <span className="text-[10px]">accept</span>
                          </label>
                        </div>
                        <div className="col-span-1 flex items-center justify-end gap-1">
                          {isDirty(sid) && (
                            <Button size="icon" variant="ghost"
                              className="h-6 w-6 text-emerald-600"
                              onClick={() => saveRow(row)}
                              data-testid={`ah-save-${sid}`}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost"
                            className="h-6 w-6 text-red-500"
                            onClick={() => deleteRow(row)}
                            data-testid={`ah-delete-${sid}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Manual add form */}
                  <div className="grid grid-cols-12 gap-2 items-center rounded border border-dashed px-2 py-2 text-xs bg-slate-50">
                    <div className="col-span-3">
                      <Input placeholder="+ Add physically-found item"
                        className="h-7 text-xs"
                        value={addForms[head]?.asset_name || ''}
                        onChange={(e) => setAddForms(p => ({ ...p, [head]: { ...(p[head] || {}), asset_name: e.target.value } }))}
                        data-testid={`ah-add-name-${head}`} />
                    </div>
                    <div className="col-span-1">
                      <Input placeholder="unit" className="h-7 text-xs"
                        value={addForms[head]?.unit || ''}
                        onChange={(e) => setAddForms(p => ({ ...p, [head]: { ...(p[head] || {}), unit: e.target.value } }))} />
                    </div>
                    <div className="col-span-1">
                      <Input placeholder="qty" type="number" className="h-7 text-xs text-right"
                        value={addForms[head]?.quantity_physical || ''}
                        onChange={(e) => setAddForms(p => ({ ...p, [head]: { ...(p[head] || {}), quantity_physical: e.target.value } }))} />
                    </div>
                    <div className="col-span-1" />
                    <div className="col-span-2">
                      <Select value={addForms[head]?.condition_physical || ''}
                        onValueChange={(v) => setAddForms(p => ({ ...p, [head]: { ...(p[head] || {}), condition_physical: v } }))}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="condition" /></SelectTrigger>
                        <SelectContent>
                          {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Input placeholder="remarks (why physical-only)"
                        className="h-7 text-xs"
                        value={addForms[head]?.remarks || ''}
                        onChange={(e) => setAddForms(p => ({ ...p, [head]: { ...(p[head] || {}), remarks: e.target.value } }))} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="sm" variant="outline"
                        onClick={() => addManualRow(head)}
                        data-testid={`ah-add-btn-${head}`}>
                        <Plus className="w-3 h-3 mr-0.5" /> Add
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <DialogFooter className="mt-2 pt-3 border-t">
        <div className="flex-1 text-[10px] text-slate-400 italic">
          Rows are auto-populated from Inventory &amp; Assets and current Consumption stock. Physical qty / condition / remarks / acceptance are preserved across Refresh. Every write is audited.
        </div>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </DialogFooter>
    </>
  );
}
